import { Box, Image, SegmentedControl, Switch, Text, TextInput, UnstyledButton } from "@dehub/theme";
import type { UseFormReturnType } from "@dehub/theme";
import { useTranslation } from "react-i18next";
import bz from "@/features/business/business.module.css";
import type { IReference } from "@/models/Reference";
import { ImageUpload, type IFile } from "@/shared/upload/image-upload";
import { MultiImageUpload } from "@/shared/upload/multi-image-upload";
import { bannersFromImages } from "@/shared/upload/banners";
import classes from "../branch.module.css";
import { MAX_BANNERS, NAME_MAX, type BranchInfoValues, type BranchMarketing } from "./branch-form-model";

export type BusinessOption = { id: string; refCode: string; name: string };

type Props = Readonly<{
  form: UseFormReturnType<BranchInfoValues>;
  readOnly: boolean;
  branchTypes: IReference[];
  buyerOptions: BusinessOption[];
  supplierOptions: BusinessOption[];
  marketing: BranchMarketing;
  /** Bumped after every fetch: MultiImageUpload only reads initialData on mount, so it remounts. */
  version: number;
  branchId: string | null;
  onMarketingChange: (next: BranchMarketing) => void;
}>;

type PickerProps = Readonly<{
  label: string;
  options: BusinessOption[];
  selected: string[];
  error?: React.ReactNode;
  readOnly: boolean;
  onToggle: (id: string) => void;
}>;

/** Business chips as toggle buttons (aria-pressed); read-only shows the chosen ones only. */
function BusinessPicker({ label, options, selected, error, readOnly, onToggle }: PickerProps) {
  const { t } = useTranslation("sector");
  const visible = readOnly ? options.filter((o) => selected.includes(o.id)) : options;

  return (
    <Box component="div" role="group" aria-label={label} className={classes.switchLabel}>
      <Text component="span" className={bz.caps}>
        {label}
      </Text>
      {visible.length > 0 ? (
        <Box className={classes.pickRow}>
          {visible.map((option) => (
            <UnstyledButton
              key={option.id}
              className={classes.pick}
              aria-pressed={selected.includes(option.id)}
              disabled={readOnly}
              onClick={() => onToggle(option.id)}
            >
              <Text component="span" className={classes.pickCode} translate="no">
                {option.refCode}
              </Text>
              {option.name}
            </UnstyledButton>
          ))}
        </Box>
      ) : null}
      {options.length === 0 ? (
        <Text className={classes.fieldError} role="alert">
          {t("form.noBusinesses")}
        </Text>
      ) : error ? (
        <Text className={classes.fieldError} role="alert">
          {error}
        </Text>
      ) : null}
    </Box>
  );
}

export function BranchInfoStep({
  form,
  readOnly,
  branchTypes,
  buyerOptions,
  supplierOptions,
  marketing,
  version,
  branchId,
  onMarketingChange,
}: Props) {
  const { t } = useTranslation("sector");

  const toggle = (field: "buyers" | "suppliers", id: string) => {
    const current = form.values[field];
    form.setFieldValue(field, current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
  };

  return (
    <Box className={bz.stack}>
      <Box className={bz.fieldGrid}>
        <Text component="span" className={`${bz.caps} ${bz.fieldWide}`}>
          {t("form.networkInfo")}
        </Text>
        <TextInput
          required
          name="name"
          maxLength={NAME_MAX}
          autoComplete="off"
          label={t("form.branchName")}
          readOnly={readOnly}
          {...form.getInputProps("name")}
        />
        <TextInput
          required
          name="nameEng"
          maxLength={NAME_MAX}
          spellCheck={false}
          autoComplete="off"
          label={t("form.branchNameLatin")}
          placeholder="Central Branch"
          readOnly={readOnly}
          {...form.getInputProps("nameEng")}
        />
        <Box className={bz.fieldWide}>
          <Text component="span" display="block" id="branch-type-label" className={bz.caps}>
            {t("form.type")}
          </Text>
          <SegmentedControl
            mt={6}
            aria-labelledby="branch-type-label"
            readOnly={readOnly}
            value={form.values.type}
            onChange={(next) => form.setFieldValue("type", next)}
            classNames={{ root: bz.segmentRoot, indicator: bz.segmentIndicator, label: bz.segmentLabel }}
            data={branchTypes.map((type) => ({ value: type.code, label: type.name }))}
          />
          {form.errors.type ? (
            <Text className={classes.fieldError} role="alert">
              {form.errors.type}
            </Text>
          ) : null}
        </Box>
      </Box>

      <Box className={bz.capsRow}>
        <Text component="span" className={bz.caps}>
          {t("form.banner")}
        </Text>
        <Text component="span" className={bz.hint}>
          {t("form.bannerHint")}
        </Text>
      </Box>
      {readOnly ? (
        marketing.banners.length > 0 ? (
          <Box className={classes.gallery}>
            {marketing.banners.map((banner) => (
              <Box key={banner.url} className={classes.galleryItem}>
                <Image src={banner.url} alt="" w="100%" h="100%" fit="cover" />
              </Box>
            ))}
          </Box>
        ) : (
          <Text className={bz.hint}>{t("form.noImages")}</Text>
        )
      ) : (
        <MultiImageUpload
          key={`banners-${branchId ?? "new"}-${version}`}
          initialData={marketing.banners}
          max={MAX_BANNERS}
          onChange={(images) => onMarketingChange({ ...marketing, banners: bannersFromImages(images) })}
        />
      )}

      <Text component="span" className={bz.caps}>
        {t("form.logo")}
      </Text>
      <Box className={bz.logoRow}>
        {readOnly ? (
          marketing.logo ? (
            <Box className={classes.galleryItem} w={96}>
              <Image src={marketing.logo} alt="" w="100%" h="100%" fit="cover" />
            </Box>
          ) : null
        ) : (
          <ImageUpload
            size={96}
            radius="lg"
            value={marketing.logo}
            onChange={(file: IFile) => onMarketingChange({ ...marketing, logo: file.thumbnail ?? null })}
          />
        )}
        <Text component="span" className={bz.hint}>
          {t("form.logoHint")}
        </Text>
      </Box>

      <Text component="span" className={bz.caps}>
        {t("form.participation")}
      </Text>
      <Box className={classes.switchCard}>
        <Switch
          name="isBuyer"
          disabled={readOnly}
          checked={form.values.isBuyer}
          onChange={(event) => form.setFieldValue("isBuyer", event.currentTarget.checked)}
          label={
            <Box component="span" className={classes.switchLabel}>
              <Text component="span" className={classes.switchTitle}>
                {t("form.doBuyer")}
              </Text>
              <Text component="span" className={classes.switchSub}>
                {t("form.doBuyerSub")}
              </Text>
            </Box>
          }
        />
        {form.values.isBuyer ? (
          <BusinessPicker
            label={t("form.buyerBiz")}
            options={buyerOptions}
            selected={form.values.buyers}
            error={form.errors.buyers}
            readOnly={readOnly}
            onToggle={(id) => toggle("buyers", id)}
          />
        ) : null}
      </Box>
      <Box className={classes.switchCard}>
        <Switch
          name="isSupplier"
          disabled={readOnly}
          checked={form.values.isSupplier}
          onChange={(event) => form.setFieldValue("isSupplier", event.currentTarget.checked)}
          label={
            <Box component="span" className={classes.switchLabel}>
              <Text component="span" className={classes.switchTitle}>
                {t("form.doSupplier")}
              </Text>
              <Text component="span" className={classes.switchSub}>
                {t("form.doSupplierSub")}
              </Text>
            </Box>
          }
        />
        {form.values.isSupplier ? (
          <BusinessPicker
            label={t("form.supplierBiz")}
            options={supplierOptions}
            selected={form.values.suppliers}
            error={form.errors.suppliers}
            readOnly={readOnly}
            onToggle={(id) => toggle("suppliers", id)}
          />
        ) : null}
      </Box>
    </Box>
  );
}
